import { useCallback, useEffect, useState } from 'react';
import { adminApi } from './api';
import QuestionEditor from './QuestionEditor';
import BulkQuestionImport from './BulkQuestionImport';
import BulkCatalogImport from './BulkCatalogImport';
import ModuleQuickCreate from './ModuleQuickCreate';
import useAdminConfirm from './useAdminConfirm';

const PAGE_SIZE = 30;
const EMPTY_FILTERS = { search: '', moduleId: '', access: '', status: '' };
const EMPTY_NEW = { module_id: '', domaine_slug: '', titre: '', access_tier: 'free', display_order: 1 };
const ACCESS = [['free', 'Free'], ['premium', 'Premium']];
const STATUS = { draft: 'Brouillon', published: 'Publié', archived: 'Archivé' };

function Badge({ value, type }) {
  const label = type === 'status' ? STATUS[value] || value : value === 'premium' ? 'Premium' : 'Free';
  return <span className={'bo-badge bo-badge--' + (type === 'status' ? value : value === 'premium' ? 'premium' : 'free')}>{label}</span>;
}

function SortHeader({ label, field, sort, onSort }) {
  const current = field === sort.key;
  return (
    <button type="button" className="bo-sort-button" onClick={() => onSort(field)}
      aria-label={'Trier par ' + label}>
      {label} <span className={current ? 'bo-sort-indicator bo-sort-indicator--active' : 'bo-sort-indicator'}>
        {current ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date.toLocaleString('fr-FR') : '—';
}

function ChoiceList({ choices, reveal = true }) {
  return (
    <ol className="bo-quiz-choices">
      {(choices || []).map((choice, index) => (
        <li key={choice.id || index}>
          {reveal && choice.is_correct && <strong aria-label="Bonne réponse">✓ </strong>}
          {choice.choice_text ?? choice.text}
        </li>
      ))}
    </ol>
  );
}

export default function QuizManager({ catalog, refresh, runAction, onUnauthorized }) {
  const modules = catalog?.modules || [];
  const activeModules = modules.filter((module) => module.is_active !== false);
  // The existing general catalogue remains available for suggestions, not pagination.
  const knownSubjects = catalog?.quizSubjects || [];
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ key: 'updated_at', direction: 'desc' });
  const [offset, setOffset] = useState(0);
  const [listVersion, setListVersion] = useState(0);
  const [results, setResults] = useState({ items: [], total: 0 });
  const [loadingList, setLoadingList] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newSubject, setNewSubject] = useState(EMPTY_NEW);
  const [selectedId, setSelectedId] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [detailVersion, setDetailVersion] = useState(0);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState('questions');
  const [info, setInfo] = useState({ titre: '', description: '', display_order: 0 });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [duplicatingQuestion, setDuplicatingQuestion] = useState(null);
  const [showCorrections, setShowCorrections] = useState(false);
  const [busy, setBusy] = useState(false);
  const { askConfirm, confirmDialog } = useAdminConfirm();
  const [error, setError] = useState('');

  const selected = bundle?.subject || null;
  const questions = bundle?.questions || [];
  const isDraft = selected?.publication_status === 'draft';

  // Each request has a cancellation guard: switching filters or subjects cannot
  // overwrite the new view with the response from a previous selection.
  useEffect(() => {
    let active = true;
    setLoadingList(true);
    adminApi.quizSubjects({
      ...appliedFilters, sort: sort.key, order: sort.direction,
      limit: PAGE_SIZE, offset,
    }).then((value) => {
      if (!active) return;
      setResults(value || { items: [], total: 0 });
    }).catch((err) => {
      if (!active) return;
      if (err.status === 401) onUnauthorized?.();
      else setError(err.message || 'Chargement des sujets impossible.');
    }).finally(() => { if (active) setLoadingList(false); });
    return () => { active = false; };
  }, [appliedFilters, sort, offset, listVersion, onUnauthorized]);

  useEffect(() => {
    let active = true;
    if (!selectedId) { setBundle(null); return undefined; }
    setLoadingDetail(true);
    // Keep the current subject visible during a same-subject refresh.
    // This prevents a successful inline edit from collapsing the full list.
    setBundle((current) =>
      Number(current?.subject?.id) === Number(selectedId) ? current : null
    );
    adminApi.quizQuestions(selectedId).then((value) => {
      if (!active) return;
      setBundle(value);
      setInfo({
        titre: value.subject?.titre || '',
        description: value.subject?.description || '',
        display_order: value.subject?.display_order ?? 0,
      });
    }).catch((err) => {
      if (!active) return;
      if (err.status === 401) onUnauthorized?.();
      else setError(err.message || 'Impossible de charger les questions.');
    }).finally(() => { if (active) setLoadingDetail(false); });
    return () => { active = false; };
  }, [selectedId, detailVersion, onUnauthorized]);

  const reload = useCallback(async () => {
    setListVersion((value) => value + 1);
    setDetailVersion((value) => value + 1);
    await refresh?.();
  }, [refresh]);

  function openSubject(id, { startEditor = false } = {}) {
    setSelectedId(id);
    setDetailTab('questions');
    setEditingQuestion(null);
    setDuplicatingQuestion(null);
    setEditorOpen(startEditor);
    setShowCorrections(false);
    setError('');
  }

  function closeSubject() {
    setSelectedId(null);
    setBundle(null);
    setEditingQuestion(null);
    setDuplicatingQuestion(null);
    setEditorOpen(false);
    setError('');
    setListVersion((value) => value + 1);
  }

  function applyFilters(event) {
    event.preventDefault();
    setOffset(0);
    setAppliedFilters({ ...filters });
  }

  function resetFilters() {
    setFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setOffset(0);
  }

  function changeSort(key) {
    setOffset(0);
    setSort((current) => ({
      key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  }

  async function createSubject(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    let created = null;
    try {
      const ok = await runAction(async () => {
        created = await adminApi.createQuizSubject({
          ...newSubject, module_id: Number(newSubject.module_id),
          display_order: Number(newSubject.display_order) || 0,
        });
      }, 'Sujet Quiz créé en brouillon.');
      if (ok && created?.id) {
        setCreateOpen(false);
        setNewSubject({ ...EMPTY_NEW });
        openSubject(created.id, { startEditor: true });
        await reload();
      }
    } finally { setBusy(false); }
  }

  async function updateMeta(patch, message = 'Sujet Quiz mis à jour.') {
    if (!selected) return false;
    setBusy(true);
    try {
      const ok = await runAction(
        () => adminApi.updateQuizSubject(selected.id, patch), message
      );
      if (ok) await reload();
      return ok;
    } finally { setBusy(false); }
  }

  function saveInfo(event) {
    event.preventDefault();
    if (!isDraft) return;
    updateMeta({
      titre: info.titre, description: info.description,
      display_order: Number(info.display_order) || 0,
    }, 'Informations du sujet enregistrées.');
  }

  async function saveQuestion(payload) {
    if (!isDraft || !selected) return;
    setBusy(true);
    try {
      const ok = await runAction(
        () => editingQuestion
          ? adminApi.updateQuestion(editingQuestion.id, payload)
          : adminApi.createQuizQuestion(selected.id, payload),
        editingQuestion ? 'Question mise à jour.' : 'Question ajoutée.'
      );
      if (ok) {
        setEditingQuestion(null);
        setDuplicatingQuestion(null);
        setEditorOpen(false);
        await reload();
      }
    } finally { setBusy(false); }
  }

  async function archiveQuestion(question) {
    if (!isDraft || !(await askConfirm({
      title: 'Retirer cette question ?',
      description: 'La question sera retirée du contenu actif. Le reste du sujet sera conservé.',
      confirmLabel: 'Retirer la question',
      intent: 'danger',
    }))) return;
    setBusy(true);
    try {
      const ok = await runAction(
        () => adminApi.archiveQuestion(question.id),
        'Question retirée du contenu actif.'
      );
      if (ok) {
        setEditingQuestion(null);
        setDuplicatingQuestion(null);
        setEditorOpen(false);
        await reload();
      }
    } finally { setBusy(false); }
  }

  function editQuestion(question, duplicate = false) {
    // Editing is inline, at the precise position of the selected question.
    // Duplicating remains a creation action in the separate form above the list.
    setEditingQuestion(duplicate ? null : question);
    setDuplicatingQuestion(duplicate ? question : null);
    setEditorOpen(duplicate);
    setDetailTab('questions');
  }

  function cancelQuestionEdit() {
    if (busy) return;
    setEditingQuestion(null);
    setDuplicatingQuestion(null);
    setEditorOpen(false);
  }

  function startNewQuestion() {
    setEditingQuestion(null);
    setDuplicatingQuestion(null);
    setEditorOpen(true);
    setDetailTab('questions');
  }

  const domains = [...new Set(knownSubjects
    .filter((item) => String(item.module_id) === String(newSubject.module_id))
    .map((item) => item.domaine_slug)
    .filter(Boolean))].sort();

  return (
    <div className="bo-stack bo-quiz-workspace">
      {confirmDialog}
      <header className="bo-row bo-row--between bo-quiz-header">
        <div>
          <p className="bo-eyebrow">Contenus</p>
          <h2>Quiz</h2>
          <p className="bo-muted">Matière → domaine → sujet → questions → choix et correction.</p>
        </div>
        {!selectedId && <button className="bo-button bo-button--primary" type="button"
          onClick={() => setCreateOpen((value) => !value)}>
          {createOpen ? 'Fermer la création' : '+ Nouveau sujet'}
        </button>}
      </header>

      {!selectedId && <BulkCatalogImport kind="quiz_subjects" onImported={reload} />}
      {error && <div role="alert" className="bo-alert bo-alert--error">{error}</div>}

      {!selectedId && (
        <>
          {createOpen && (
            <section className="bo-panel bo-quiz-create">
              <h3>Nouveau sujet Quiz</h3>
              <p className="bo-muted">Le sujet sera créé en brouillon, puis son éditeur de questions s’ouvrira.</p>
              <form className="bo-form bo-quiz-info-form" onSubmit={createSubject}>
                <label>Matière
                  <select className="bo-input" required value={newSubject.module_id}
                    onChange={(event) => setNewSubject((s) => ({ ...s, module_id: event.target.value, domaine_slug: '' }))}>
                    <option value="">Choisir…</option>
                    {activeModules.map((m) => <option key={m.id} value={m.id}>{m.matiere}</option>)}
                  </select>
                </label>
                <label>Domaine
                  <input className="bo-input" list="bo-quiz-existing-domains" required
                    placeholder="Ex. algèbre (ou domaine existant)"
                    value={newSubject.domaine_slug}
                    onChange={(event) => setNewSubject((s) => ({ ...s, domaine_slug: event.target.value }))}/>
                  <datalist id="bo-quiz-existing-domains">
                    {domains.map((domain) => <option key={domain} value={domain} />)}
                  </datalist>
                  <small className="bo-muted">Sélectionner un domaine existant ou en saisir un nouveau.</small>
                </label>
                <label>Titre du sujet
                  <input className="bo-input" required value={newSubject.titre}
                    onChange={(event) => setNewSubject((s) => ({ ...s, titre: event.target.value }))}/>
                </label>
                <label>Accès
                  <select className="bo-input" value={newSubject.access_tier}
                    onChange={(event) => setNewSubject((s) => ({ ...s, access_tier: event.target.value }))}>
                    {ACCESS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                <div className="bo-span-2">
                  <ModuleQuickCreate onCreated={async (module) => {
                    setNewSubject((s) => ({ ...s, module_id: String(module.id), domaine_slug: '' }));
                    await refresh?.();
                  }}/>
                </div>
                <div className="bo-actions bo-span-2">
                  <button type="button" className="bo-button bo-button--ghost"
                    onClick={() => setCreateOpen(false)}>Annuler</button>
                  <button className="bo-button bo-button--primary" disabled={busy}>
                    {busy ? 'Création…' : 'Créer et ajouter les questions'}
                  </button>
                </div>
              </form>
            </section>
          )}

          <section className="bo-panel bo-quiz-catalog">
            <div className="bo-panel__header">
              <div>
                <h3>Catalogue des sujets Quiz</h3>
                <p className="bo-muted">{results.total} sujet(s) correspondant aux critères.</p>
              </div>
              <span className="bo-kpi">{loadingList ? 'Chargement…' : results.total + ' résultat(s)'}</span>
            </div>
            <form className="bo-quiz-filters" onSubmit={applyFilters}>
              <label>Recherche
                <input className="bo-input" placeholder="Titre, domaine, matière…"
                  value={filters.search}
                  onChange={(event) => setFilters((s) => ({ ...s, search: event.target.value }))}/>
              </label>
              <label>Matière
                <select className="bo-input" value={filters.moduleId}
                  onChange={(event) => setFilters((s) => ({ ...s, moduleId: event.target.value }))}>
                  <option value="">Toutes</option>
                  {activeModules.map((m) => <option key={m.id} value={m.id}>{m.matiere}</option>)}
                </select>
              </label>
              <label>Accès
                <select className="bo-input" value={filters.access}
                  onChange={(event) => setFilters((s) => ({ ...s, access: event.target.value }))}>
                  <option value="">Tous</option>
                  {ACCESS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label>Publication
                <select className="bo-input" value={filters.status}
                  onChange={(event) => setFilters((s) => ({ ...s, status: event.target.value }))}>
                  <option value="">Tous les états</option>
                  {Object.entries(STATUS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <div className="bo-actions bo-quiz-filter-actions">
                <button className="bo-button bo-button--primary">Rechercher</button>
                <button type="button" className="bo-button bo-button--ghost" onClick={resetFilters}>Réinitialiser</button>
              </div>
            </form>
            <div className="bo-quiz-grid-scroll">
              <table className="bo-table bo-quiz-grid">
                <thead>
                  <tr>
                    <th><SortHeader label="Sujet" field="title" sort={sort} onSort={changeSort}/></th>
                    <th><SortHeader label="Matière" field="module" sort={sort} onSort={changeSort}/></th>
                    <th><SortHeader label="Domaine" field="domain" sort={sort} onSort={changeSort}/></th>
                    <th><SortHeader label="Accès" field="access" sort={sort} onSort={changeSort}/></th>
                    <th><SortHeader label="État" field="status" sort={sort} onSort={changeSort}/></th>
                    <th><SortHeader label="Questions" field="questions" sort={sort} onSort={changeSort}/></th>
                    <th><SortHeader label="Mise à jour" field="updated_at" sort={sort} onSort={changeSort}/></th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(results.items || []).map((item) => (
                    <tr key={item.id}>
                      <td><strong>{item.titre}</strong>
                        {item.description && <small className="bo-cell-ellipsis">{item.description}</small>}
                      </td>
                      <td>{item.module_title}</td>
                      <td>{item.domaine_slug}</td>
                      <td><Badge value={item.access_tier} type="access"/></td>
                      <td><Badge value={item.publication_status} type="status"/></td>
                      <td className="bo-cell-number">{item.question_count}</td>
                      <td>{formatDate(item.updated_at)}</td>
                      <td><button className="bo-button bo-button--ghost bo-button--compact" type="button"
                        onClick={() => openSubject(item.id)}>Ouvrir</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loadingList && <div className="bo-loading">Chargement du catalogue…</div>}
              {!loadingList && !(results.items || []).length &&
                <div className="bo-empty">Aucun sujet correspondant aux critères.</div>}
            </div>
            <div className="bo-row bo-row--between bo-pagination">
              <small className="bo-muted">
                {results.total
                  ? (offset + 1) + '–' + Math.min(offset + (results.items || []).length, results.total) + ' sur ' + results.total
                  : '0 sujet'}
              </small>
              <div className="bo-actions">
                <button type="button" className="bo-button bo-button--ghost"
                  disabled={loadingList || offset === 0}
                  onClick={() => setOffset((n) => Math.max(0, n - PAGE_SIZE))}>Précédent</button>
                <button type="button" className="bo-button bo-button--ghost"
                  disabled={loadingList || offset + (results.items || []).length >= results.total}
                  onClick={() => setOffset((n) => n + PAGE_SIZE)}>Suivant</button>
              </div>
            </div>
          </section>
        </>
      )}

      {selectedId && (
        <section className="bo-panel bo-quiz-detail">
          <button type="button" className="bo-button bo-button--ghost bo-quiz-back"
            onClick={closeSubject}>Retour au catalogue</button>
          {loadingDetail && !selected ? (
            <div className="bo-loading">Chargement du sujet…</div>
          ) : selected ? (
            <>
              <div className="bo-panel__header bo-quiz-detail-head">
                <div>
                  <p className="bo-eyebrow">Sujet Quiz #{selected.id}</p>
                  <h3>{selected.titre}</h3>
                  <p className="bo-muted">{selected.module_title} · {selected.domaine_slug} · {questions.length} question(s)</p>
                </div>
                <div className="bo-inline-controls">
                  <Badge value={selected.publication_status} type="status"/>
                  <label className="bo-quiz-access-control">Accès
                    <select className="bo-input bo-select" aria-label="Accès du sujet"
                      value={selected.access_tier} disabled={busy}
                      onChange={(event) => updateMeta({ access_tier: event.target.value }, 'Accès mis à jour.')}>
                      {ACCESS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
              </div>
              <div className="bo-quiz-lifecycle">
                <div>
                  <strong>Cycle éditorial</strong>
                  <p className="bo-muted">Les questions sont modifiables uniquement en brouillon. Les tentatives historiques sont préservées.</p>
                </div>
                <div className="bo-actions">
                  {isDraft && <>
                    <button type="button" className="bo-button bo-button--primary" disabled={busy || !questions.length}
                      onClick={() => updateMeta({ publication_status: 'published' }, 'Sujet publié.')}>Publier</button>
                    <button type="button" className="bo-button bo-button--danger-ghost" disabled={busy}
                      onClick={() => updateMeta({ publication_status: 'archived' }, 'Sujet archivé.')}>Archiver</button>
                  </>}
                  {selected.publication_status === 'published' && <>
                    <button type="button" className="bo-button bo-button--ghost" disabled={busy}
                      onClick={() => updateMeta({ publication_status: 'draft' }, 'Sujet repassé en brouillon.')}>Repasser en brouillon</button>
                    <button type="button" className="bo-button bo-button--danger-ghost" disabled={busy}
                      onClick={() => updateMeta({ publication_status: 'archived' }, 'Sujet archivé.')}>Archiver</button>
                  </>}
                  {selected.publication_status === 'archived' &&
                    <button type="button" className="bo-button bo-button--primary" disabled={busy}
                      onClick={() => updateMeta({ publication_status: 'draft' }, 'Sujet restauré en brouillon.')}>Restaurer en brouillon</button>}
                </div>
              </div>
              {isDraft && <BulkQuestionImport kind="quiz" subjectId={selected.id} onImported={reload} />}
              <div className="bo-tabs bo-quiz-tabs">
                <button type="button" className={detailTab === 'questions' ? 'is-active' : ''}
                  onClick={() => setDetailTab('questions')}>Questions ({questions.length})</button>
                <button type="button" className={detailTab === 'information' ? 'is-active' : ''}
                  onClick={() => setDetailTab('information')}>Informations du sujet</button>
                <button type="button" className={detailTab === 'preview' ? 'is-active' : ''}
                  onClick={() => setDetailTab('preview')}>Prévisualisation</button>
              </div>

              {detailTab === 'questions' && (
                <div className="bo-stack">
                  <div className="bo-row bo-row--between bo-quiz-section-head">
                    <div>
                      <h4>Questions du sujet</h4>
                      <p className="bo-muted">Rédiger l’énoncé, choisir la bonne réponse et renseigner la correction.</p>
                    </div>
                    {isDraft && !editorOpen && !editingQuestion &&
                      <button type="button" className="bo-button bo-button--primary"
                        onClick={startNewQuestion}>+ Ajouter une question</button>}
                  </div>
                  {!questions.length && !editorOpen && <div className="bo-empty">
                    Aucune question. Ajoutez une première question avant publication.
                  </div>}
                  {editorOpen && isDraft && !editingQuestion && (
                    <div className="bo-editor-block bo-quiz-question-editor">
                      <h4>{duplicatingQuestion ? 'Dupliquer la question' : 'Ajouter une question'}</h4>
                      {duplicatingQuestion && <p className="bo-muted">
                        Une copie indépendante sera créée : l’ancienne question et ses tentatives resteront inchangées.
                      </p>}
                      <QuestionEditor key={String(selectedId) + ':new:' + (duplicatingQuestion?.id || '')}
                        initialQuestion={duplicatingQuestion}
                        onSave={saveQuestion} busy={busy}
                        onCancel={cancelQuestionEdit}/>
                    </div>
                  )}
                  <div className="bo-quiz-question-list">
                    {questions.map((question, index) => {
                      const isEditingHere = isDraft && Number(editingQuestion?.id) === Number(question.id);
                      return (
                        <article
                          className={isEditingHere ? 'bo-question-card bo-quiz-question-card--editing' : 'bo-question-card'}
                          key={question.id}
                        >
                          {isEditingHere ? (
                            <div className="bo-quiz-inline-editor">
                              <div className="bo-quiz-inline-editor__header">
                                <h4>Modifier la question</h4>
                                <p className="bo-muted">Question {index + 1} sur {questions.length} — édition à cet emplacement</p>
                              </div>
                              <QuestionEditor
                                key={String(selectedId) + ':edit:' + question.id}
                                initialQuestion={question}
                                onSave={saveQuestion}
                                busy={busy}
                                onCancel={cancelQuestionEdit}
                              />
                            </div>
                          ) : (
                            <>
                              <div className="bo-row bo-row--between bo-quiz-question-head">
                                <div>
                                  <strong>Q{index + 1}. {question.question_text}</strong>
                                  <p className="bo-muted">
                                    {question.difficulte || 'Difficulté non renseignée'}
                                    {question.notion_cible ? ' · ' + question.notion_cible : ''}
                                  </p>
                                </div>
                                {isDraft && !editingQuestion && !editorOpen && <div className="bo-actions">
                                  <button type="button" className="bo-button bo-button--ghost" disabled={busy}
                                    onClick={() => editQuestion(question)}>Modifier</button>
                                  <button type="button" className="bo-button bo-button--ghost" disabled={busy}
                                    onClick={() => editQuestion(question, true)}>Dupliquer</button>
                                  <button type="button" className="bo-button bo-button--danger-ghost" disabled={busy}
                                    onClick={() => archiveQuestion(question)}>Retirer</button>
                                </div>}
                              </div>
                              <ChoiceList choices={question.choices}/>
                              {question.explication && <p className="bo-muted">Correction : {question.explication}</p>}
                              {question.is_time_sensitive && <small className="bo-muted">À revoir régulièrement : sensible à l’actualité</small>}
                            </>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}

              {detailTab === 'information' && (
                <section className="bo-quiz-info-panel">
                  <h4>Informations du sujet</h4>
                  <form className="bo-form bo-quiz-info-form" onSubmit={saveInfo}>
                    <label>Matière
                      <input className="bo-input" value={selected.module_title || ''} readOnly
                        title="Le rattachement à la matière est conservé pour protéger les questions existantes."/>
                    </label>
                    <label>Domaine
                      <input className="bo-input" value={selected.domaine_slug || ''} readOnly
                        title="Le domaine est conservé pour protéger les questions existantes."/>
                    </label>
                    <label>Titre du sujet
                      <input className="bo-input" required value={info.titre} disabled={!isDraft}
                        onChange={(event) => setInfo((s) => ({ ...s, titre: event.target.value }))}/>
                    </label>
                    <label>Ordre d’affichage
                      <input className="bo-input" type="number" min="0" value={info.display_order} disabled={!isDraft}
                        onChange={(event) => setInfo((s) => ({ ...s, display_order: event.target.value }))}/>
                    </label>
                    <label className="bo-span-2">Description
                      <textarea className="bo-input" rows="3" value={info.description} disabled={!isDraft}
                        onChange={(event) => setInfo((s) => ({ ...s, description: event.target.value }))}/>
                    </label>
                    <p className="bo-muted bo-span-2">
                      Matière et domaine restent en lecture seule : les questions sont rattachées à ces identifiants.
                      L’accès Free/Premium et le cycle éditorial se gèrent dans l’en-tête.
                    </p>
                    <div className="bo-actions bo-span-2">
                      {isDraft
                        ? <button className="bo-button bo-button--primary" disabled={busy}>Enregistrer les informations</button>
                        : <p className="bo-muted">Repassez le sujet en brouillon pour modifier ses informations.</p>}
                    </div>
                  </form>
                </section>
              )}

              {detailTab === 'preview' && (
                <section className="bo-quiz-preview">
                  <div className="bo-row bo-row--between bo-quiz-section-head">
                    <div>
                      <h4>Prévisualisation du sujet</h4>
                      <p className="bo-muted">Aperçu éditorial du questionnaire, sans lancement d’une tentative candidat.</p>
                    </div>
                    <button type="button" className="bo-button bo-button--ghost"
                      onClick={() => setShowCorrections((visible) => !visible)}>
                      {showCorrections ? 'Masquer les corrections' : 'Afficher les corrections'}
                    </button>
                  </div>
                  {questions.map((question, index) => (
                    <article className="bo-question-card" key={question.id}>
                      <strong>{index + 1}. {question.question_text}</strong>
                      <ChoiceList choices={question.choices} reveal={showCorrections}/>
                      {showCorrections && question.explication && <p className="bo-muted">
                        Correction : {question.explication}
                      </p>}
                    </article>
                  ))}
                  {!questions.length && <div className="bo-empty">Aucune question à prévisualiser.</div>}
                </section>
              )}
            </>
          ) : !loadingDetail ? <div className="bo-empty">Le sujet n’a pas pu être chargé.</div> : null}
        </section>
      )}
    </div>
  );
}
